import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Alert } from "../components/ui";

const ERRORS: Record<string, string> = {
  wrong_domain: "Please sign in with your University of Auckland account.",
  email_not_verified: "That Google account has no verified email address.",
  invalid_state: "Your sign-in attempt expired. Please try again.",
  exchange_failed: "We could not complete sign-in with Google. Please try again.",
  link_invalid: "That sign-in link has expired or has already been used.",
  account_disabled: "That account is not active. Contact the internship coordinator.",
};

export default function SignIn() {
  const [params] = useSearchParams();
  const audience = params.get("as");
  const error = params.get("error");

  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: () => api.get<{ demoMode: boolean; googleEnabled: boolean }>("/config"),
    staleTime: Infinity,
  });

  // Arriving from a landing-page button shows only the relevant method.
  const showUniversity = audience !== "industry";
  const showIndustry = audience === "industry" || audience === null;

  return (
    <div className="mx-auto max-w-md px-4 py-12">
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-3">
          <span aria-hidden className="grid h-10 w-10 place-items-center rounded bg-uoa-blue font-bold text-white">
            IS
          </span>
          <span className="text-2xl font-semibold text-heading">Intern-Seek</span>
        </Link>
        <p className="mt-2 text-uoa-grey">University of Auckland internship placements</p>
      </div>

      {error && <Alert tone="error">{ERRORS[error] ?? "Sign-in failed. Please try again."}</Alert>}

      {config && !config.googleEnabled && showUniversity && (
        <Alert>
          Google sign-in is not configured on this environment yet.
          {config.demoMode && <> Use the <Link className="underline" to="/demo">demo sign-in</Link> instead.</>}
        </Alert>
      )}

      {showUniversity && (
        <div className="card mb-6">
          <h1 className="mb-1 text-lg font-semibold">
            {audience === "staff" ? "University staff" : "Students and university staff"}
          </h1>
          <p className="mb-4 text-sm text-uoa-grey">Use your @aucklanduni.ac.nz account.</p>
          <a href="/api/auth/google/start" className="btn-primary w-full">Continue with Google</a>
        </div>
      )}

      {showIndustry && <IndustrySignIn />}

      <p className="mt-8 text-center text-sm">
        <Link to="/" className="text-uoa-blue underline dark:text-uoa-blue-light">Back</Link>
        {config?.demoMode && (
          <>
            {" · "}
            <Link to="/demo" className="text-uoa-blue underline dark:text-uoa-blue-light">Explore the demo</Link>
          </>
        )}
      </p>
    </div>
  );
}

function IndustrySignIn() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/magic-link", { email });
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2 className="mb-1 text-lg font-semibold">Employers</h2>
      {sent ? (
        <Alert tone="success">
          If that address belongs to a registered organisation, a sign-in link is on its way. It expires in
          15 minutes.
        </Alert>
      ) : (
        <>
          <p className="mb-4 text-sm text-uoa-grey">We will email you a single-use sign-in link.</p>
          <form onSubmit={requestLink}>
            <label className="label" htmlFor="email">Work email address</label>
            <input
              id="email"
              type="email"
              required
              className="input mb-3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <button type="submit" className="btn-secondary w-full" disabled={busy}>
              {busy ? "Sending..." : "Email me a sign-in link"}
            </button>
          </form>

          {/* No "Continue with Google" for employers: the OAuth client is an
              Internal app on the aucklanduni.ac.nz Workspace, so Google itself
              refuses any outside account. Employers sign in by emailed link. */}
          <p className="mt-4 text-sm text-uoa-grey">
            New organisation? <Link className="text-uoa-blue underline dark:text-uoa-blue-light" to="/register">Register your interest</Link>.
          </p>
        </>
      )}
    </div>
  );
}
